import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, Star } from 'lucide-react';
import { useCartStore } from '../../store/cartStore';
import { motion, AnimatePresence } from 'framer-motion';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const { items } = useCartStore();
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Birth Charts', path: '/services/birth-chart' },
    { name: 'Zodiac Jewelry', path: '/shop' },
    { name: 'Consultations', path: '/services/consultations' },
    { name: 'Cosmic Map', path: '/cosmic-map' },
  ];

  const cartCount = items.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-midnight/60 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative flex items-center justify-center">
              <Star className="w-7 h-7 text-gold animate-pulse-slow" />
              <div className="absolute inset-0 bg-gold/20 blur-xl rounded-full group-hover:bg-gold/40 transition-all duration-500" />
            </div>
            <span className="text-xl font-serif font-bold tracking-[0.15em] text-white uppercase">
              Astro<span className="text-gold italic">Vidya</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-[11px] font-bold uppercase tracking-[0.2em] transition-all duration-300 hover:text-gold relative group ${
                  location.pathname === link.path ? 'text-gold' : 'text-gray-400'
                }`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 h-[1px] bg-gold transition-all duration-300 ${
                  location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center space-x-6">
            <Link to="/account" className="text-gray-400 hover:text-gold transition-colors duration-300">
              <User className="w-5 h-5" />
            </Link>
            <Link to="/cart" className="relative text-gray-400 hover:text-gold transition-colors duration-300">
              <ShoppingCart className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-gold text-midnight text-[9px] font-black w-4 h-4 flex items-center justify-center rounded-full shadow-lg shadow-gold/20">
                  {cartCount}
                </span>
              )}
            </Link>
            <button
              className="md:hidden text-gray-400 hover:text-gold transition-colors duration-300"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden bg-midnight/95 backdrop-blur-2xl border-b border-white/5 overflow-hidden"
          >
            <div className="px-6 pt-4 pb-10 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block py-4 text-sm font-bold uppercase tracking-[0.2em] transition-all ${
                    location.pathname === link.path ? 'text-gold' : 'text-gray-400'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
